import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Avatar, DropdownButtonContext, DropdownSelect, Tooltip, } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import * as React from 'react';
import { shallowEqual } from 'react-redux';
import { useDebounce } from 'react-use';
import { analytics } from 'app/utils/segment';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { useCommandHandler } from 'utils/commands';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { getUserDisplayName } from 'utils/user/getUserDisplayName';
import { ConfirmTransferModal } from './components/confirmTransferModal/confirmTransferModal';
import { RequestTransferAssistButton } from './components/requestTransferAssistButton/requestTransferAssistButton';
import { TransferToAgentSelectItem } from './components/transferToAgentSelectItem/transferToAgentSelectItem';
import styles from './transferToAgentDropdown.module.scss';
import { comparePresenceStatus } from './utils/comparePresenceStatus';
import { isAgentAvailableForCall } from './utils/isAgentAvailableForCall';
import { sortTransferToAgentItems } from './utils/sortTransferToAgentItems';
export const TransferToAgentDropdown = () => {
    const [transferToUser, setTransferToUser] = useState();
    const [searchQuery, setSearchQuery] = useState();
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState();
    const { assignedAgent, metaConversation, transferToAgent, conversationAsyncStatus, } = useAppSelector((state) => {
        var _a;
        return ({
            assignedAgent: (_a = state.conversation.metaConversation.conversation.assignment) === null || _a === void 0 ? void 0 : _a.agent,
            metaConversation: state.conversation.metaConversation,
            conversationAsyncStatus: state.conversation.asyncStatus.getConversation,
            transferToAgent: state.transferToAgent,
        });
    }, shallowEqual);
    const inProgress = useMemo(() => {
        return !!transferToAgent.transferRequests.find((request) => {
            var _a, _b;
            return ((_b = (_a = request === null || request === void 0 ? void 0 : request.metaConversation) === null || _a === void 0 ? void 0 : _a.conversation) === null || _b === void 0 ? void 0 : _b.id) ===
                metaConversation.conversation.id;
        });
    }, [transferToAgent, metaConversation]);
    const { allPresence: presences } = useAppSelector((state) => ({
        allPresence: state.presence.allPresence,
    }));
    useDebounce(() => {
        if (searchQuery !== undefined) {
            setDebouncedSearchQuery(searchQuery);
        }
    }, 150, [searchQuery]);
    const items = useMemo(() => {
        if (!presences) {
            return [];
        }
        const channel = metaConversation.conversation.initial_channel;
        return presences
            .filter((presence) => presence.presence.status !== 'offline')
            .map((presence) => {
            const canBeCalled = isAgentAvailableForCall(presence.presence);
            const showCallButton = channel === 'pstnphone' &&
                canBeCalled &&
                presence.user.id !== (assignedAgent === null || assignedAgent === void 0 ? void 0 : assignedAgent.id);
            return {
                id: presence.user.id,
                name: getUserDisplayName(presence.user),
                status: presence.presence.status,
                avatarUrl: presence.user.avatar_url,
                presence: presence.presence,
                actions: showCallButton && (_jsx(RequestTransferAssistButton, { user: presence.user })),
            };
        })
            .sort(comparePresenceStatus);
    }, [presences, metaConversation, assignedAgent]);
    const filteredItemsBySearchQuery = useMemo(() => {
        if (!(debouncedSearchQuery === null || debouncedSearchQuery === void 0 ? void 0 : debouncedSearchQuery.length)) {
            return items;
        }
        return items.filter((item) => item.name
            .toLowerCase()
            .includes(debouncedSearchQuery.trim().toLowerCase()));
    }, [items, debouncedSearchQuery]);
    const disabledItemsIds = useMemo(() => {
        const channel = metaConversation.conversation.initial_channel;
        let disabledPresences = [];
        if (channel === 'pstnphone' || channel === 'callback') {
            const disabledPresencesForPhone = filteredItemsBySearchQuery
                .filter((item) => !isAgentAvailableForCall(item.presence))
                .map((item) => item.id);
            disabledPresences = [...disabledPresences, ...disabledPresencesForPhone];
        }
        return assignedAgent
            ? [assignedAgent.id, ...disabledPresences]
            : disabledPresences;
    }, [filteredItemsBySearchQuery, assignedAgent, metaConversation]);
    const filteredAndSortedByDisabledItemsIds = useMemo(() => {
        return sortTransferToAgentItems(filteredItemsBySearchQuery, disabledItemsIds);
    }, [filteredItemsBySearchQuery, disabledItemsIds]);
    const handleOnToggle = (isOpen) => {
        if (!isOpen) {
            setSearchQuery(undefined);
            setDebouncedSearchQuery(undefined);
        }
        else {
            analytics.conversationHeaderTransferToAgentMenuOpened();
        }
    };
    const handleOnAgentSelect = (selectItem) => {
        analytics.conversationHeaderTransferToAgentItemSelected();
        setTransferToUser(selectItem);
        setSearchQuery(undefined);
        setDebouncedSearchQuery(undefined);
    };
    const emptyMessage = (debouncedSearchQuery === null || debouncedSearchQuery === void 0 ? void 0 : debouncedSearchQuery.length)
        ? 'No search results...'
        : 'No online agents...';
    const dropdownButtonClassName = classNames({
        [styles.dropdownButtonHasAvatar]: !inProgress,
    }, styles.dropdownButton);
    const tooltipContent = (_jsx(CommandKeybindingsTooltipContent, Object.assign({ commandName: "conversation:transferToAgent" }, { children: "Transfer to agent" })));
    return (_jsxs(_Fragment, { children: [_jsx(Tooltip, Object.assign({ dark: true, content: tooltipContent, disabled: inProgress, wrap: "div", wrapProps: {
                    className: styles.tooltip,
                } }, { children: _jsxs(DropdownSelect, Object.assign({ items: filteredAndSortedByDisabledItemsIds, itemComponent: TransferToAgentSelectItem, selectedItems: (assignedAgent === null || assignedAgent === void 0 ? void 0 : assignedAgent.id) ? [assignedAgent === null || assignedAgent === void 0 ? void 0 : assignedAgent.id] : undefined, disabledItems: disabledItemsIds, searchPlaceholder: "Search for online agents...", onSelect: handleOnAgentSelect, onToggle: handleOnToggle, onSearch: (value) => setSearchQuery(value !== undefined ? value : ''), hasSearch: true, noArrow: inProgress ? true : false, emptyMessage: emptyMessage, fixed: true, className: styles.dropdownSelect, dropdownProps: {
                        className: styles.dropdown,
                    }, buttonProps: {
                        variant: 'tertiary',
                        disabled: inProgress,
                        isLoading: conversationAsyncStatus === 'loading',
                        className: dropdownButtonClassName,
                        slotStart: inProgress ? undefined : (_jsx(Avatar, { user: assignedAgent, size: "24" })),
                    } }, { children: [!inProgress && (_jsx(DropdownButtonContext.Consumer, { children: ({ dropdownOpen, openDropdown, closeDropdown }) => (_jsx(TransferToAgentCommandHandler, { actionFn: () => {
                                    if (!dropdownOpen) {
                                        openDropdown();
                                    }
                                    else {
                                        closeDropdown();
                                    }
                                    handleOnToggle(!dropdownOpen);
                                } })) })), inProgress && 'Transfer in progress...', !inProgress && assignedAgent && `${assignedAgent.name}`, !inProgress && !assignedAgent && 'Unassigned'] })) })), assignedAgent && transferToUser && metaConversation && (_jsx(ConfirmTransferModal, { assignedAgent: assignedAgent, transferToUser: transferToUser, onClose: () => setTransferToUser(undefined), isOpen: !!transferToUser, metaConversation: metaConversation }))] }));
};
const TransferToAgentCommandHandler = ({ actionFn, }) => {
    useCommandHandler({
        name: 'conversation:transferToAgent',
        actionFn: () => {
            actionFn();
        },
    });
    return null;
};
